import { useState } from 'react';
import BulkAppealModal from '../../modals/BulkAppealModal';
import BulkAcknowledgeModal from '../../modals/BulkAcknowledgeModal';
import { Check, Gavel, X } from 'lucide-react';
import type { DeliveryBulkSelection } from './useDeliveryBulkSelection';

interface DeliveryBulkBarProps {
  bulk: DeliveryBulkSelection;
  /** The viewer is acting as a supplier — decides the appeal outcome copy. */
  isSupplierView: boolean;
  /** Called after a bulk action succeeds, so the parent can clear and refetch. */
  onDone: () => void;
}

const BTN = 'inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg text-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed';

/**
 * The Deliveries grid's bulk action bar (shown once rows are selected) and its
 * two confirm modals.
 *
 * CR-7420: "Appeal Selected" — one "delivered on time" appeal per row.
 * CR-8214: "Acknowledge Selected" (customer side) — what the per-row
 * Acknowledge tick does, for every selected row, without an appeal. Each
 * button counts and acts on only the selected rows it applies to.
 */
export default function DeliveryBulkBar({ bulk, isSupplierView, onDone }: DeliveryBulkBarProps) {
  const [open, setOpen] = useState<'acknowledge' | 'appeal' | null>(null);
  const { selectedIds, canAcknowledge, acknowledgeGrns, appealGrns, clear } = bulk;
  const ackCount = acknowledgeGrns.length;
  const appealCount = appealGrns.length;

  return (
    <>
      {selectedIds.size > 0 && (
        <div className="mx-4 mb-2 flex items-center justify-between gap-3 rounded-lg border border-orange-200 bg-orange-50 px-4 py-2">
          <span className="text-sm font-medium text-orange-800">{selectedIds.size} selected</span>
          <div className="flex items-center gap-2">
            <button
              onClick={clear}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-sm rounded-lg border bg-white text-gray-600 hover:bg-muted transition-colors"
            >
              <X className="h-4 w-4" />
              Clear
            </button>
            {canAcknowledge && (
              <button
                onClick={() => setOpen('acknowledge')}
                disabled={ackCount === 0}
                title={ackCount === 0 ? 'None of the selected deliveries can be acknowledged' : undefined}
                className={`${BTN} bg-green-600 hover:bg-green-700`}
              >
                <Check className="h-4 w-4" />
                Acknowledge Selected ({ackCount})
              </button>
            )}
            <button
              onClick={() => setOpen('appeal')}
              disabled={appealCount === 0}
              title={appealCount === 0 ? 'None of the selected deliveries can be appealed' : undefined}
              className={`${BTN} bg-orange-600 hover:bg-orange-700`}
            >
              {/* CR-7319: the appeal gavel, as on each row. */}
              <Gavel className="h-4 w-4" />
              Appeal Selected ({appealCount})
            </button>
          </div>
        </div>
      )}

      {open === 'acknowledge' && ackCount > 0 && (
        <BulkAcknowledgeModal
          isOpen={true}
          grns={acknowledgeGrns}
          onClose={() => setOpen(null)}
          onSuccess={onDone}
        />
      )}
      {open === 'appeal' && appealCount > 0 && (
        <BulkAppealModal
          isOpen={true}
          grns={appealGrns}
          // CR-7420: a customer-raised appeal auto-accepts server-side, so the
          // modal must not promise that the customer will review it. Keyed on
          // the view for the same reason bulkModeActive is.
          raisedByCustomer={!isSupplierView}
          onClose={() => setOpen(null)}
          onSuccess={onDone}
        />
      )}
    </>
  );
}
